import { Link } from "react-router-dom";
import { MapPin, BedDouble, Bath, Users, Wifi, WashingMachine, Car, ChefHat, ArrowUpRight } from "lucide-react";

const FEATURES = [
  { icon: Wifi, label: "High-Speed Wi-Fi" },
  { icon: ChefHat, label: "Kitchen Access" },
  { icon: WashingMachine, label: "Laundry Access" },
  { icon: Car, label: "Free Parking" },
];

export const PropertyCard = ({ property, index = 0 }) => {
  return (
    <div
      data-testid={`unit-card-${property.slug}`}
      className="group flex flex-col bg-white border border-[#E7E5E4] rounded-2xl overflow-hidden transition-transform duration-500 hover:-translate-y-2"
    >
      <Link to={`/properties/${property.slug}`} className="relative aspect-[4/3] overflow-hidden block" data-testid={`unit-image-${property.slug}`}>
        <img
          src={property.cover}
          alt={property.name}
          loading={index < 3 ? "eager" : "lazy"}
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
        {property.isPremium && (
          <span data-testid={`premium-badge-${property.slug}`} className="absolute top-4 left-4 rounded-full bg-[#1C1917]/90 backdrop-blur-sm text-white text-[0.65rem] tracking-[0.2em] uppercase px-3 py-1.5 font-medium">
            Premium
          </span>
        )}
      </Link>

      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-[#8A9A86] mb-3">
          <MapPin className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-xs tracking-[0.14em] uppercase">{property.area}</span>
        </div>

        <h3 className="text-xl font-normal tracking-tight text-[#1C1917] mb-4">{property.name}</h3>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5 text-sm text-[#44403C]">
          {property.bedrooms != null ? (
            <>
              <span className="flex items-center gap-2"><BedDouble className="w-4 h-4 text-[#8A9A86]" strokeWidth={1.5} /> {property.bedrooms} {property.bedrooms === 1 ? "Bedroom" : "Bedrooms"}</span>
              {property.bathrooms != null && <span className="flex items-center gap-2"><Bath className="w-4 h-4 text-[#8A9A86]" strokeWidth={1.5} /> {property.bathrooms} {property.bathrooms === 1 ? "Bath" : "Baths"}</span>}
              {property.maxGuests != null && <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#8A9A86]" strokeWidth={1.5} /> Sleeps {property.maxGuests}</span>}
            </>
          ) : (
            <>
              <span className="flex items-center gap-2"><BedDouble className="w-4 h-4 text-[#8A9A86]" strokeWidth={1.5} /> Fully Furnished</span>
              <span className="flex items-center gap-2"><Bath className="w-4 h-4 text-[#8A9A86]" strokeWidth={1.5} /> {property.bathroom}</span>
            </>
          )}
        </div>

        {(property.sqft || property.monthlyRate) && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-5 text-sm" data-testid={`unit-meta-${property.slug}`}>
            {property.sqft ? <span className="text-[#57534E] font-light">{Number(property.sqft).toLocaleString()} sq ft</span> : null}
            {property.monthlyRate ? <span className="text-[#1C1917] font-medium">${Number(property.monthlyRate).toLocaleString()}<span className="text-[#78716C] font-light">/mo</span></span> : null}
          </div>
        )}

        <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-6">
          {FEATURES.map((f) => (
            <span key={f.label} className="flex items-center gap-2 text-sm text-[#57534E] font-light">
              <f.icon className="w-4 h-4 text-[#8A9A86] shrink-0" strokeWidth={1.5} /> {f.label}
            </span>
          ))}
        </div>

        <div className="mt-auto flex items-center gap-3">
          <Link
            to={`/properties/${property.slug}`}
            data-testid={`view-details-${property.slug}`}
            className="flex-1 text-center rounded-full bg-[#1C1917] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#44403C] transition-colors duration-300"
          >
            View Details
          </Link>
          <Link
            to="/book"
            data-testid={`book-${property.slug}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#E7E5E4] text-[#1C1917] px-5 py-2.5 text-sm font-medium hover:bg-[#F5F5F4] transition-colors duration-300"
          >
            Request to Book <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
          </Link>
        </div>
      </div>
    </div>
  );
};
